import axios from 'axios';

// Base URL for the weather API
const API_BASE_URL = 'https://weather-api-python.oskarmansanqu.workers.dev';

// Create axios instance with default config
const weatherAPI = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for logging
weatherAPI.interceptors.request.use(
  (config) => {
    console.log(`Making request to: ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
weatherAPI.interceptors.response.use(
  (response) => {
    console.log(`Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    console.error('Response error:', error);
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data.error || `Server error: ${error.response.status}`);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Network error: Unable to connect to weather service');
    } else {
      // Something else happened
      throw new Error('Request failed: ' + error.message);
    }
  }
);

/**
 * Get current weather for a specific city
 * @param {string} city - City name
 * @returns {Promise<Object>} Weather data
 */
export const getCurrentWeather = async (city = 'London') => {
  try {
    const endpoint = city === 'London' ? '/weather/current' : `/weather/city/${encodeURIComponent(city)}`;
    const response = await weatherAPI.get(endpoint);
    
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching current weather:', error);
    throw error;
  }
};

/**
 * Get 5-day weather forecast
 * @param {string} city - City name (optional, defaults to London)
 * @returns {Promise<Object>} Forecast data
 */
export const getForecast = async (city = 'London') => {
  try {
    const response = await weatherAPI.get('/weather/forecast');
    
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching forecast:', error);
    throw error;
  }
};

/**
 * Get health status of the API
 * @returns {Promise<Object>} Health data
 */
export const getHealthStatus = async () => {
  try {
    const response = await weatherAPI.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error fetching health status:', error);
    throw error;
  }
};

/**
 * Get debug information from the API
 * @returns {Promise<Object>} Debug data
 */
export const getDebugInfo = async () => {
  try {
    const response = await weatherAPI.get('/debug');
    return response.data;
  } catch (error) {
    console.error('Error fetching debug info:', error);
    throw error;
  }
};

/**
 * Format temperature for display
 * @param {number} temp - Temperature in Celsius
 * @returns {string} Formatted temperature
 */
export const formatTemperature = (temp) => {
  return `${Math.round(temp)}°C`;
};

/**
 * Format wind speed for display
 * @param {number} speed - Wind speed in m/s
 * @returns {string} Formatted wind speed
 */
export const formatWindSpeed = (speed) => {
  return `${Math.round(speed * 3.6)} km/h`; // Convert m/s to km/h
};

/**
 * Format wind direction for display
 * @param {number} degrees - Wind direction in degrees
 * @returns {string} Formatted wind direction
 */
export const formatWindDirection = (degrees) => {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
};

/**
 * Get weather icon URL from OpenWeatherMap
 * @param {string} iconCode - Icon code from API
 * @returns {string} Icon URL
 */
export const getWeatherIconUrl = (iconCode) => {
  return `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
};

/**
 * Get weather emoji based on description
 * @param {string} description - Weather description
 * @returns {string} Weather emoji
 */
export const getWeatherEmoji = (description) => {
  const emojiMap = {
    'clear sky': '☀️',
    'few clouds': '⛅',
    'scattered clouds': '⛅',
    'broken clouds': '☁️',
    'overcast clouds': '☁️',
    'shower rain': '🌦️',
    'rain': '🌧️',
    'thunderstorm': '⛈️',
    'snow': '❄️',
    'mist': '🌫️',
    'fog': '🌫️',
    'haze': '🌫️',
  };
  
  return emojiMap[description.toLowerCase()] || '🌤️';
};
